import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileImage, FileText, X } from 'lucide-react';

import {
  extractPortfolioFromImage,
  MAX_PORTFOLIO_IMAGE_BYTES,
  type PortfolioSnapshot,
} from '../../../services/PortfolioImageService';
import { MAX_PORTFOLIO_POSITIONS } from './portfolioSheet';
import styles from './ExcelUploadModal.module.css';

const IMAGE_ACCEPT = 'image/jpeg,image/png,.jpg,.jpeg,.png';
const PDF_ACCEPT = 'application/pdf,.pdf';

const IMAGE_EXTENSIONS: readonly string[] = ['.jpg', '.jpeg', '.png'];
const PDF_EXTENSIONS: readonly string[] = ['.pdf'];

const MAX_IMAGE_MB = MAX_PORTFOLIO_IMAGE_BYTES / (1024 * 1024);

type PickedKind = 'image' | 'pdf';

const hasExtension = (file: File, extensions: readonly string[]): boolean =>
  extensions.some((ext) => file.name.toLowerCase().endsWith(ext));

const sizeLabel = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export interface ExcelUploadModalProps {
  open: boolean;
  onCancel: () => void;
  onExtracted: (snapshot: PortfolioSnapshot) => void;
}

export const ExcelUploadModal: React.FC<ExcelUploadModalProps> = ({
  open,
  onCancel,
  onExtracted,
}) => {
  const [picked, setPicked] = useState<{ file: File; kind: PickedKind } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const abortInFlight = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    abortInFlight();
    onCancel();
  }, [abortInFlight, onCancel]);

  useEffect(() => {
    if (!open) return undefined;
    setPicked(null);
    setError(null);
    setBusy(false);
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancel();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, cancel]);

  useEffect(() => () => abortRef.current?.abort(), []);

  if (!open) return null;

  const pickFile = (
    event: React.ChangeEvent<HTMLInputElement>,
    kind: PickedKind,
  ): void => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const extensions = kind === 'image' ? IMAGE_EXTENSIONS : PDF_EXTENSIONS;
    if (!hasExtension(file, extensions)) {
      setError(
        kind === 'image'
          ? 'Only JPEG or PNG files are allowed.'
          : 'Only PDF files are allowed.',
      );
      return;
    }
    if (kind === 'image' && file.size > MAX_PORTFOLIO_IMAGE_BYTES) {
      setError(
        `That image is ${sizeLabel(file.size)}. Upload a lighter one, under ${MAX_IMAGE_MB} MB.`,
      );
      return;
    }

    setError(null);
    setPicked({ file, kind });
  };

  const removePicked = (): void => {
    abortInFlight();
    setBusy(false);
    setPicked(null);
    setError(null);
  };

  const readPortfolio = async (): Promise<void> => {
    if (!picked) return;
    if (picked.kind === 'pdf') {
      setError('PDF reading is not available yet. Upload a JPEG or PNG for now.');
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setBusy(true);
    try {
      const snapshot = await extractPortfolioFromImage(picked.file, controller.signal);
      if (snapshot.positions.length === 0) {
        setError('No stock positions found in that image.');
        return;
      }
      if (snapshot.positions.length > MAX_PORTFOLIO_POSITIONS) {
        setError(
          `That image holds ${snapshot.positions.length} positions. The sheet fits ${MAX_PORTFOLIO_POSITIONS}.`,
        );
        return;
      }
      onExtracted(snapshot);
    } catch (caught) {
      if (controller.signal.aborted) return;
      setError(caught instanceof Error ? caught.message : 'Could not read that image.');
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setBusy(false);
    }
  };

  const handleBackdropClick = (event: React.MouseEvent): void => {
    if (event.target === event.currentTarget) cancel();
  };

  return (
    <div
      className={styles.backdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
      data-testid="excel-upload-modal"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="excel-upload-modal-title"
      >
        <h2 id="excel-upload-modal-title" className={styles.title}>
          Upload your Portfolio
        </h2>
        <p className={styles.subtitle}>
          Pick the kind of file to upload. Keep images under {MAX_IMAGE_MB} MB.
        </p>

        {picked ? (
          <div
            className={`${styles.chip} ${busy ? styles.chipBusy : ''}`}
            data-testid="excel-upload-chip"
          >
            <span className={styles.chipIcon} aria-hidden="true">
              {picked.kind === 'image' ? <FileImage size={16} /> : <FileText size={16} />}
            </span>
            <span className={styles.chipText}>
              <span className={styles.chipName} title={picked.file.name}>
                {picked.file.name}
              </span>
              <span className={styles.chipMeta}>
                {busy ? 'Reading your portfolio…' : sizeLabel(picked.file.size)}
              </span>
            </span>
            <button
              type="button"
              className={styles.chipRemove}
              onClick={removePicked}
              aria-label="Remove file"
              title="Remove file"
              data-testid="excel-upload-chip-remove"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <div className={styles.choices}>
            <button
              type="button"
              className={styles.choiceButton}
              onClick={() => imageInputRef.current?.click()}
              data-testid="excel-upload-choose-image"
            >
              <FileImage size={20} aria-hidden="true" />
              <span>JPEG / PNG</span>
            </button>
            <button
              type="button"
              className={styles.choiceButton}
              onClick={() => pdfInputRef.current?.click()}
              data-testid="excel-upload-choose-pdf"
            >
              <FileText size={20} aria-hidden="true" />
              <span>PDF</span>
            </button>
          </div>
        )}

        {error ? (
          <p className={styles.error} role="alert" data-testid="excel-upload-error">
            {error}
          </p>
        ) : null}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={cancel}
            data-testid="excel-upload-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={readPortfolio}
            disabled={!picked || busy}
            data-testid="excel-upload-read"
          >
            {busy ? 'Reading…' : 'Read portfolio'}
          </button>
        </div>

        <input
          ref={imageInputRef}
          type="file"
          className={styles.hiddenInput}
          accept={IMAGE_ACCEPT}
          onChange={(event) => pickFile(event, 'image')}
          data-testid="excel-upload-input-image"
        />
        <input
          ref={pdfInputRef}
          type="file"
          className={styles.hiddenInput}
          accept={PDF_ACCEPT}
          onChange={(event) => pickFile(event, 'pdf')}
          data-testid="excel-upload-input-pdf"
        />
      </div>
    </div>
  );
};

export default ExcelUploadModal;
